/**
 * Themed-shape variant — per-category palettes.
 *
 * Six categories, six bespoke palettes. Each is a warm illustrated mood:
 *   - Garden   = warm greens + cream
 *   - Music    = burnt amber + warm wood + ivory
 *   - Kitchen  = soft butter yellow + cream + sage
 *   - Films    = warm crimson + cream + brass
 *   - Travel   = pale blue-grey + warm sand + ivory
 *   - Animals  = warm tan + forest + cream
 *
 * Each palette carries:
 *   - `paper`     — the surface color
 *   - `ink`       — body type + cell letters
 *   - `accent`    — the celebratory glow + selection warmth
 *   - `accent2`   — secondary accent for motifs
 *   - `glow`      — bright found-word color
 *   - `quiet`     — gentler ink for de-emphasised states
 *
 * Two slices: `light` (day) and `dark` (lamp-lit at dusk).
 */

import type { CategorySlug } from './themes';

export type ThemeSlug = CategorySlug;

export interface ThemedPalette {
  paper: string;
  paperWarm: string;
  paperDeep: string;
  ink: string;
  inkSoft: string;
  inkFaint: string;
  accent: string;
  accent2: string;
  glow: string;
  quiet: string;
}

export interface ThemeColors {
  light: ThemedPalette;
  dark: ThemedPalette;
}

// ---------------------------------------------------------------------------
// The six per-theme palettes.
// ---------------------------------------------------------------------------

export const THEME_PALETTES: Record<ThemeSlug, ThemeColors> = {
  garden: {
    light: {
      paper: '#F1ECDD',
      paperWarm: '#E6DFC9',
      paperDeep: '#D6CDB2',
      ink: '#33402B',
      inkSoft: '#5C6A4F',
      inkFaint: '#8A9379',
      accent: '#6F8B4F',
      accent2: '#B4865B',
      glow: '#C3D387',
      quiet: '#7A8F6F',
    },
    dark: {
      paper: '#181F17',
      paperWarm: '#222A1E',
      paperDeep: '#2D3826',
      ink: '#E8E2C9',
      inkSoft: '#B5BFA0',
      inkFaint: '#7F8A6E',
      accent: '#A8C779',
      accent2: '#D2A86C',
      glow: '#E0EE9E',
      quiet: '#88A06E',
    },
  },
  music: {
    light: {
      paper: '#F4EAD7',
      paperWarm: '#EADEC2',
      paperDeep: '#D9C8A2',
      ink: '#2D1F14',
      inkSoft: '#5B402E',
      inkFaint: '#8A6E55',
      accent: '#B25E2A',
      accent2: '#7A4928',
      glow: '#F1BC6B',
      quiet: '#8A5F38',
    },
    dark: {
      paper: '#1B130C',
      paperWarm: '#2A1E14',
      paperDeep: '#3A281A',
      ink: '#F1E2C5',
      inkSoft: '#C9AE82',
      inkFaint: '#8E7857',
      accent: '#E08542',
      accent2: '#C0703A',
      glow: '#FFCE82',
      quiet: '#B58356',
    },
  },
  kitchen: {
    light: {
      paper: '#F5EFD8',
      paperWarm: '#ECE4C0',
      paperDeep: '#DDD09F',
      ink: '#2E2A1F',
      inkSoft: '#5C5538',
      inkFaint: '#908452',
      accent: '#B79141',
      accent2: '#7A8F6F',
      glow: '#F0D27A',
      quiet: '#8A8054',
    },
    dark: {
      paper: '#1A180F',
      paperWarm: '#262218',
      paperDeep: '#352F20',
      ink: '#F1ECD2',
      inkSoft: '#C7BC8E',
      inkFaint: '#8C835B',
      accent: '#E0B85F',
      accent2: '#93A881',
      glow: '#FFD683',
      quiet: '#B5A668',
    },
  },
  films: {
    light: {
      paper: '#F2E8DC',
      paperWarm: '#E5D5C0',
      paperDeep: '#CFBBA0',
      ink: '#2A1815',
      inkSoft: '#56342E',
      inkFaint: '#8B6258',
      accent: '#A0382C',
      accent2: '#B58A3E',
      glow: '#E48662',
      quiet: '#7A4A40',
    },
    dark: {
      paper: '#160F0D',
      paperWarm: '#221814',
      paperDeep: '#33221C',
      ink: '#F0E2D2',
      inkSoft: '#C8AE99',
      inkFaint: '#917867',
      accent: '#D75A47',
      accent2: '#E0B257',
      glow: '#F49E80',
      quiet: '#A0786A',
    },
  },
  travel: {
    light: {
      paper: '#EDE7DA',
      paperWarm: '#DCD3BF',
      paperDeep: '#C5BBA1',
      ink: '#1F2A3A',
      inkSoft: '#4A5570',
      inkFaint: '#7B83A0',
      accent: '#3F5A8A',
      accent2: '#B0743C',
      glow: '#8FA8D0',
      quiet: '#5A6F8E',
    },
    dark: {
      paper: '#10141C',
      paperWarm: '#181E2A',
      paperDeep: '#212A3A',
      ink: '#E5E8F0',
      inkSoft: '#B0BBD4',
      inkFaint: '#7C88A8',
      accent: '#86A4D4',
      accent2: '#D89460',
      glow: '#B5C9EC',
      quiet: '#8FA0C0',
    },
  },
  animals: {
    light: {
      paper: '#F0E9D6',
      paperWarm: '#E2D8BC',
      paperDeep: '#C9BA98',
      ink: '#2A2418',
      inkSoft: '#564A33',
      inkFaint: '#8D7E5D',
      accent: '#7A5A2E',
      accent2: '#5C7A4F',
      glow: '#CFA764',
      quiet: '#7C6A4B',
    },
    dark: {
      paper: '#161210',
      paperWarm: '#211B16',
      paperDeep: '#322820',
      ink: '#EFE2C8',
      inkSoft: '#C5B08E',
      inkFaint: '#8E7B5C',
      accent: '#C99560',
      accent2: '#8AA174',
      glow: '#E5BD7E',
      quiet: '#A78768',
    },
  },
};

/** Resolve the active slice for a theme + global theme (light/dark). */
export function paletteFor(
  themeSlug: ThemeSlug,
  mode: 'light' | 'dark',
): ThemedPalette {
  return THEME_PALETTES[themeSlug][mode];
}

/**
 * Inject a theme palette into the DOM as CSS variables. Used by the Game
 * surface to switch on the active theme without re-rendering the world.
 */
export function paletteToCssVars(p: ThemedPalette): Record<string, string> {
  return {
    '--th-paper': p.paper,
    '--th-paper-warm': p.paperWarm,
    '--th-paper-deep': p.paperDeep,
    '--th-ink': p.ink,
    '--th-ink-soft': p.inkSoft,
    '--th-ink-faint': p.inkFaint,
    '--th-accent': p.accent,
    '--th-accent-2': p.accent2,
    '--th-glow': p.glow,
    '--th-quiet': p.quiet,
  };
}
